import { StyledCreateEditUserDrawer, StyledCreateEditUserForm } from './style';
import { Button, Checkbox, Icon, Input, Spinner } from '../../../../../ui';
import { useIntl } from 'react-intl';
import useQueryApiClient from '../../../../../utils/useQueryApiClient';
import { message } from 'antd';
import { ContactProps } from '../../../../../interfaces/BackendModels';
import { useForm } from 'antd/es/form/Form';

interface CreateEditProps {
  open: boolean;
  onClose: () => void;
  editContactId: number | null;
  setReloadCount: React.Dispatch<React.SetStateAction<number>>;
  stableFilter: {
    farm_id?: number;
  };
}

export const CreateEditUser = ({
  open,
  onClose,
  editContactId,
  setReloadCount,
  stableFilter,
}: CreateEditProps) => {
  const intl = useIntl();
  const [form] = useForm();

  const { appendData: storeContact } = useQueryApiClient({
    request: {
      url: '/api/v2/farm/contacts',
      method: 'POST',
    },
    onSuccess: (response: ContactProps) => handleSuccess(response),
  });

  const { appendData: editContact } = useQueryApiClient({
    request: {
      url: `/api/v2/farm/contacts/:id?_method=PATCH`,
      method: 'PATCH',
    },
    onSuccess: (response: ContactProps) => handleSuccess(response),
  });

  const { isLoading: isContactLoading } = useQueryApiClient({
    request: {
      url: `/api/v2/farm/contacts/${editContactId}`,
      method: 'GET',
      disableOnMount: editContactId === null,
    },
    onSuccess: (response: ContactProps) => loadFormWithData(response),
  });

  const title = editContactId
    ? intl.formatMessage({ id: 'general.edit_contact_user' })
    : intl.formatMessage({ id: 'general.add_new_contact_user' });

  const submitText = editContactId
    ? intl.formatMessage({ id: 'general.edit' })
    : intl.formatMessage({ id: 'general.add' });

  const msg = editContactId
    ? intl.formatMessage({ id: 'message.success_edit_farm_contact' })
    : intl.formatMessage({ id: 'message.success_create_farm_contact' });

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      let values = form.getFieldsValue();

      editContactId
        ? editContact(values, { id: values.id })
        : storeContact({ ...values, ...stableFilter });
    } catch (e) {
      message.error(intl.formatMessage({ id: 'message.error' }));
    }
  };

  const loadFormWithData = (response: ContactProps) => {
    const fieldValues: Record<string, any> = {
      ...response,
    };

    form.setFieldsValue(fieldValues);
  };

  const handleSuccess = (response: ContactProps) => {
    setReloadCount((prev: number) => prev + 1);
    onClose();
    message.success(msg);
    form.resetFields();
  };

  const closeFormAndReset = () => {
    form.resetFields();
    onClose();
  };

  const Footer = () => {
    return (
      <div className={'footer'}>
        <Button
          label={intl.formatMessage({ id: 'general.cancel' })}
          onClick={closeFormAndReset}
        />
        <Button label={submitText} type={'primary'} onClick={handleSubmit} />
      </div>
    );
  };

  return (
    <StyledCreateEditUserDrawer
      footer={<Footer />}
      open={open}
      title={title}
      onClose={closeFormAndReset}
      width={800}
      closeIcon={<Icon name={'global-close-icon'} />}
      zIndex={1100}
    >
      <StyledCreateEditUserForm form={form}>
        <Spinner spinning={isContactLoading}>
          <Input name={'id'} hidden />
          <Input name={'farm_id'} hidden />

          <Input
            name={'full_name'}
            label={intl.formatMessage({ id: 'contacts.name_surname' })}
            validations={'requiredText'}
            layout={'vertical'}
          />
          <Input
            name={'contact_phone'}
            label={intl.formatMessage({ id: 'general.telephone' })}
            validations={'phoneNumber'}
            layout={'vertical'}
          />
          <Input
            name={'contact_email'}
            label={intl.formatMessage({ id: 'general.email' })}
            validations={'email'}
            layout={'vertical'}
          />
          <Input
            name={'role'}
            label={intl.formatMessage({ id: 'general.position' })}
            validations={'required'}
            layout={'vertical'}
          />
          <Checkbox
            name={'status'}
            label={intl.formatMessage({ id: 'general.active_disabled' })}
          />
        </Spinner>
      </StyledCreateEditUserForm>
    </StyledCreateEditUserDrawer>
  );
};
